<script setup>
import { useI18n } from 'vue-i18n'
import { useNotificationContent } from '@/modules/notifications/composables/use-notification-content.composable'
import { useNotificationDate } from '@/modules/notifications/composables/use-notification-date.composable'
import { useNotificationNavigation } from '@/modules/notifications/composables/use-notification-navigation.composable'
import { getNotificationModuleLabel } from '@/modules/notifications/constants/notification-modules.constant'
import { useNotificationsStore } from '@/modules/notifications/stores/notifications.store'

const emit = defineEmits(['viewAll', 'navigated'])

const { t } = useI18n()
const { formatShortDate } = useNotificationDate()
const { resolveTitle, resolveContent } = useNotificationContent()
const { openNotification } = useNotificationNavigation()
const notificationsStore = useNotificationsStore()

function handleMarkAsRead(notification) {
  notificationsStore.markAsRead(notification._id)
}

function handleMarkAllAsRead() {
  notificationsStore.markAllAsRead()
}

function handleOpen(notification) {
  emit('navigated')
  openNotification(notification)
}
</script>

<template>
  <div class="flex w-104 max-w-[85vw] flex-col">
    <div class="flex items-center justify-between px-4 pt-4 pb-2">
      <span class="font-bold text-dss-secondary-500 dark:text-dss-surface-800">
        {{ t('notifications.title') }}
      </span>
      <button
        v-if="notificationsStore.preview.length"
        type="button"
        class="text-sm font-semibold text-dss-primary-600 hover:text-dss-primary-700 cursor-pointer"
        @click="handleMarkAllAsRead"
      >
        {{ t('notifications.mark-all-as-read') }}
      </button>
    </div>

    <div
      v-if="notificationsStore.isLoadingPreview"
      class="flex justify-center px-4 py-8"
    >
      <i class="pi pi-spin pi-spinner text-dss-surface-500" />
    </div>

    <div
      v-else-if="notificationsStore.hasError"
      class="px-4 py-8 text-center text-sm text-dss-surface-600"
    >
      {{ t('notifications.error-loading') }}
    </div>

    <div
      v-else-if="!notificationsStore.preview.length"
      class="px-4 py-8 text-center text-sm text-dss-surface-600"
    >
      {{ t('notifications.empty') }}
    </div>

    <ul
      v-else
      class="flex flex-col divide-y divide-dss-surface-200"
    >
      <li
        v-for="notification in notificationsStore.preview"
        :key="notification._id"
        class="flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-dss-surface-100"
        data-testid="notification-item"
        @click="handleOpen(notification)"
      >
        <span class="mt-1.5 size-2 shrink-0 rounded-full bg-dss-primary-500" />

        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <span class="text-sm font-bold text-dss-secondary-500 dark:text-dss-surface-800 line-clamp-2">
            {{ resolveTitle(notification) }}
          </span>
          <span
            v-if="notification.module"
            class="w-fit rounded-md bg-dss-primary-50 px-2 py-0.5 text-xs font-medium text-dss-primary-700 dark:bg-dss-primary-900 dark:text-dss-primary-200"
          >
            {{ getNotificationModuleLabel(notification.module) }}
          </span>
          <span
            v-if="resolveContent(notification)"
            class="text-xs text-dss-surface-600 line-clamp-2"
          >
            {{ resolveContent(notification) }}
          </span>
        </div>

        <div class="flex shrink-0 flex-col items-end gap-2">
          <span class="text-xs text-dss-surface-500">
            {{ formatShortDate(notification) }}
          </span>
          <button
            v-tooltip.bottom="t('notifications.mark-as-read')"
            type="button"
            class="cursor-pointer text-dss-surface-400 hover:text-dss-primary-600"
            :aria-label="t('notifications.mark-as-read')"
            data-testid="mark-as-read"
            @click.stop="handleMarkAsRead(notification)"
          >
            <i class="pi pi-check text-sm" />
          </button>
        </div>
      </li>
    </ul>

    <div class="border-t border-dss-surface-200 px-4 py-3 text-center">
      <button
        type="button"
        class="text-sm font-semibold text-dss-primary-600 hover:text-dss-primary-700 cursor-pointer"
        data-testid="view-all"
        @click="emit('viewAll')"
      >
        {{ t('notifications.view-all') }}
      </button>
    </div>
  </div>
</template>
